/**
 * AdminCodes – Verwaltungsoberfläche für Gutschein-Codes
 * Erreichbar unter /admin/codes
 * Nur für eingeloggte Admins zugänglich.
 */
import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Link } from "wouter";

const C = {
  accent: "#6d0000",
  teal: "#115751",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

const BAROMETER_OPTIONS = [
  { value: "sensorik", label: "Sensorik-Barometer" },
  { value: "seo", label: "SEO-Barometer" },
  { value: "lokal", label: "Lokal-Barometer" },
  { value: "geo", label: "Geo-Barometer" },
  { value: "conversion", label: "Conversion-Barometer" },
  { value: "accessibility", label: "Accessibility-Barometer" },
  { value: "datenschutz", label: "Datenschutz-Barometer" },
  { value: "website_check", label: "Basic Website-RealitätsCheck" },
  { value: "marketing_check", label: "Pro Website-RealitätsCheck" },
  { value: "ecommerce", label: "E-Commerce-Barometer" },
  { value: "wettbewerb", label: "Wettbewerbs-Barometer" },
];

function formatDate(d: Date | null | undefined): string {
  if (!d) return "–";
  return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function isExpired(d: Date | null | undefined): boolean {
  if (!d) return false;
  return new Date(d) < new Date();
}

export default function AdminCodes() {
  const { user, loading, isAuthenticated } = useAuth();
  const isAdmin = user?.role === "admin";

  // Sicherheitshalber: ownerKey aus localStorage oder user.openId
  // Da wir OWNER_OPEN_ID nicht im Frontend haben, nutzen wir den user.openId
  // Der Server prüft ob dieser mit ENV.ownerOpenId übereinstimmt
  const ownerKey = (user as any)?.openId ?? "";

  // Formular-State
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newScopeType, setNewScopeType] = useState<"global" | "single">("global");
  const [newScopeValue, setNewScopeValue] = useState("");
  const [newMaxUses, setNewMaxUses] = useState("");
  const [newExpiresAt, setNewExpiresAt] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  // Codes laden
  const codesQuery = trpc.accessCodes.list.useQuery(
    { ownerKey },
    { enabled: !!ownerKey && isAdmin }
  );

  // Code erstellen
  const createMutation = trpc.accessCodes.create.useMutation({
    onSuccess: (data) => {
      setCreateSuccess(`Code "${data.code}" erfolgreich erstellt!`);
      setCreateError(null);
      setNewCode("");
      setNewLabel("");
      setNewScopeType("global");
      setNewScopeValue("");
      setNewMaxUses("");
      setNewExpiresAt("");
      codesQuery.refetch();
    },
    onError: (err) => {
      setCreateError(err.message);
      setCreateSuccess(null);
    },
  });

  // Code aktivieren/deaktivieren
  const toggleMutation = trpc.accessCodes.toggle.useMutation({
    onSuccess: () => codesQuery.refetch(),
  });

  // Code löschen
  const removeMutation = trpc.accessCodes.remove.useMutation({
    onSuccess: () => codesQuery.refetch(),
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setCreateSuccess(null);
    if (!newCode.trim()) return;
    createMutation.mutate({
      ownerKey,
      code: newCode.trim(),
      label: newLabel.trim() || undefined,
      scopeType: newScopeType,
      scopeValue: newScopeType === "single" ? newScopeValue : undefined,
      maxUses: newMaxUses ? parseInt(newMaxUses) : undefined,
      expiresAt: newExpiresAt ? new Date(newExpiresAt).toISOString() : undefined,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
        <p style={{ color: C.muted, fontFamily: "'DM Sans', sans-serif" }}>Lade…</p>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: C.bg }}>
        <img src="/manus-storage/29_7f3bb394.svg" alt="" className="w-10 h-10 mb-4 object-contain mx-auto" style={{ opacity: 0.85 }} />
        <h1 className="text-2xl font-black mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>
          KEIN ZUGANG
        </h1>
        <p className="text-sm mb-6" style={{ color: C.muted, fontFamily: "'DM Sans', sans-serif" }}>
          Diese Seite ist nur für Administratoren zugänglich.
        </p>
        <Link href="/">
          <a className="text-sm underline" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>
            ← Zurück zur Startseite
          </a>
        </Link>
      </div>
    );
  }

  const codes = codesQuery.data ?? [];

  return (
    <div className="min-h-screen" style={{ background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="py-8 px-4" style={{ background: C.accent, color: "#fff" }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase opacity-75 mb-1">Admin-Panel</p>
              <h1 className="text-3xl font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}>
                GUTSCHEIN-CODES VERWALTEN
              </h1>
            </div>
            <Link href="/">
              <a className="text-sm opacity-75 hover:opacity-100 underline">← Zur Website</a>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

        {/* Neuen Code erstellen */}
        <div className="border border-gray-200 p-6" style={{ background: C.bgGray }}>
          <h2 className="text-lg font-black mb-4 uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent, letterSpacing: "0.05em" }}>
            Neuen Code erstellen
          </h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div>
                <label className="block text-xs font-bold mb-1 uppercase tracking-wide" style={{ color: C.text }}>
                  Code *
                </label>
                <input
                  type="text"
                  required
                  placeholder="z.B. DEMO2024"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  style={{
                    width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`,
                    fontSize: "14px", fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: "0.05em", background: "#fff",
                  }}
                />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1 uppercase tracking-wide" style={{ color: C.text }}>
                  Internes Label (optional)
                </label>
                <input
                  type="text"
                  placeholder="z.B. Demo für Kunde Müller"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  style={{
                    width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`,
                    fontSize: "14px", fontFamily: "'DM Sans', sans-serif", background: "#fff",
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
              <div>
                <label className="block text-xs font-bold mb-1 uppercase tracking-wide" style={{ color: C.text }}>
                  Geltungsbereich
                </label>
                <select
                  value={newScopeType}
                  onChange={(e) => setNewScopeType(e.target.value as "global" | "single")}
                  style={{
                    width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`,
                    fontSize: "14px", fontFamily: "'DM Sans', sans-serif", background: "#fff",
                  }}
                >
                  <option value="global">Alle Barometer</option>
                  <option value="single">Einzelnes Barometer</option>
                </select>
              </div>

              {newScopeType === "single" && (
                <div>
                  <label className="block text-xs font-bold mb-1 uppercase tracking-wide" style={{ color: C.text }}>
                    Barometer
                  </label>
                  <select
                    value={newScopeValue}
                    onChange={(e) => setNewScopeValue(e.target.value)}
                    style={{
                      width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`,
                      fontSize: "14px", fontFamily: "'DM Sans', sans-serif", background: "#fff",
                    }}
                  >
                    <option value="">Bitte wählen</option>
                    {BAROMETER_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold mb-1 uppercase tracking-wide" style={{ color: C.text }}>
                  Max. Verwendungen
                </label>
                <input
                  type="number"
                  min="1"
                  placeholder="Unbegrenzt"
                  value={newMaxUses}
                  onChange={(e) => setNewMaxUses(e.target.value)}
                  style={{
                    width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`,
                    fontSize: "14px", fontFamily: "'DM Sans', sans-serif", background: "#fff",
                  }}
                />
              </div>

              <div>
                <label className="block text-xs font-bold mb-1 uppercase tracking-wide" style={{ color: C.text }}>
                  Ablaufdatum
                </label>
                <input
                  type="date"
                  value={newExpiresAt}
                  onChange={(e) => setNewExpiresAt(e.target.value)}
                  style={{
                    width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`,
                    fontSize: "14px", fontFamily: "'DM Sans', sans-serif", background: "#fff",
                  }}
                />
              </div>
            </div>

            {createError && (
              <p className="text-sm" style={{ color: "#dc2626" }}>{createError}</p>
            )}
            {createSuccess && (
              <p className="text-sm" style={{ color: "#16a34a" }}>✅ {createSuccess}</p>
            )}

            <button
              type="submit"
              disabled={createMutation.isPending}
              style={{
                padding: "10px 24px",
                background: createMutation.isPending ? "#9ca3af" : C.teal,
                color: "#fff", border: "none",
                fontSize: "14px", fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700, cursor: createMutation.isPending ? "not-allowed" : "pointer",
              }}
            >
              {createMutation.isPending ? "Erstelle…" : "Code erstellen"}
            </button>
          </form>
        </div>

        {/* Bestehende Codes */}
        <div>
          <h2 className="text-lg font-black mb-4 uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent, letterSpacing: "0.05em" }}>
            Bestehende Codes ({codes.length})
          </h2>

          {codesQuery.isLoading && (
            <p className="text-sm" style={{ color: C.muted }}>Lade Codes…</p>
          )}

          {codes.length === 0 && !codesQuery.isLoading && (
            <div className="p-8 text-center border border-dashed" style={{ borderColor: C.border }}>
              <p className="text-sm" style={{ color: C.muted }}>Noch keine Codes erstellt.</p>
            </div>
          )}

          {codes.length > 0 && (
            <div className="overflow-x-auto">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: C.bgGray, borderBottom: `2px solid ${C.border}` }}>
                    {["Code", "Label", "Bereich", "Verwendet", "Ablauf", "Status", "Aktionen"].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: C.text, fontFamily: "'DM Sans', sans-serif" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {codes.map((code) => {
                    const expired = isExpired(code.expiresAt);
                    const exhausted = code.maxUses !== null && code.usedCount >= code.maxUses;
                    const statusOk = code.isActive && !expired && !exhausted;
                    return (
                      <tr key={code.id} style={{ borderBottom: `1px solid ${C.border}`, opacity: (!code.isActive || expired) ? 0.5 : 1 }}>
                        <td style={{ padding: "10px 12px", fontWeight: 700, letterSpacing: "0.05em", color: C.accent, fontFamily: "monospace" }}>
                          {code.code}
                        </td>
                        <td style={{ padding: "10px 12px", color: C.muted }}>
                          {code.label || "–"}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          {code.scopeType === "global" ? (
                            <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "2px 8px", fontSize: "11px", fontWeight: 600 }}>
                              Alle
                            </span>
                          ) : (
                            <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", fontSize: "11px", fontWeight: 600 }}>
                              {BAROMETER_OPTIONS.find(o => o.value === code.scopeValue)?.label ?? code.scopeValue}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px", color: C.text }}>
                          {code.usedCount}{code.maxUses !== null ? ` / ${code.maxUses}` : ""}
                        </td>
                        <td style={{ padding: "10px 12px", color: expired ? "#dc2626" : C.muted }}>
                          {expired ? `⚠ ${formatDate(code.expiresAt)}` : formatDate(code.expiresAt)}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          {statusOk ? (
                            <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 8px", fontSize: "11px", fontWeight: 600 }}>
                              Aktiv
                            </span>
                          ) : expired ? (
                            <span style={{ background: "#fef2f2", color: "#dc2626", padding: "2px 8px", fontSize: "11px", fontWeight: 600 }}>
                              Abgelaufen
                            </span>
                          ) : exhausted ? (
                            <span style={{ background: "#fef2f2", color: "#dc2626", padding: "2px 8px", fontSize: "11px", fontWeight: 600 }}>
                              Erschöpft
                            </span>
                          ) : (
                            <span style={{ background: "#f3f4f6", color: "#6b7280", padding: "2px 8px", fontSize: "11px", fontWeight: 600 }}>
                              Inaktiv
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              onClick={() => toggleMutation.mutate({ ownerKey, id: code.id, isActive: !code.isActive })}
                              style={{
                                padding: "4px 10px", fontSize: "11px", fontWeight: 600,
                                background: code.isActive ? "#fef2f2" : "#dcfce7",
                                color: code.isActive ? "#dc2626" : "#166534",
                                border: "none", cursor: "pointer",
                              }}
                            >
                              {code.isActive ? "Deaktivieren" : "Aktivieren"}
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(`Code "${code.code}" wirklich löschen?`)) {
                                  removeMutation.mutate({ ownerKey, id: code.id });
                                }
                              }}
                              style={{
                                padding: "4px 10px", fontSize: "11px", fontWeight: 600,
                                background: "#fef2f2", color: "#dc2626",
                                border: "none", cursor: "pointer",
                              }}
                            >
                              Löschen
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
